import {
  obterIdTca,
  obterIdEmpresaReal,
  obterNomeEmpresa,
  obterDocumentoEmpresa,
} from "../utils/empresa.js";
import { obterTipoProgramaUsuarioLogado } from "../utils/tipoPrograma.js";

/**
 * Conversores entre o payload bruto do backend e o modelo simplificado
 * usado pela UI de Acompanhamento.
 */

const CHAVES_EMPRESA_EM_ITEM = ["empresa", "cadastro", "estudante"];

const CAMPOS_CNPJ_DIRETO = [
  "cnpj",
  "CNPJ",
  "cnpj_ou_cpf",
  "cnpj_empresa",
];

/**
 * Chaves aceitas como id do "registro de acompanhamento" no payload
 * consolidado de `/acompanhamentos/me` (2026-09-25). O backend pode
 * mandar `id` (id da autorização) ou `autorizacao_id`. O id do
 * TCA Empregador / TCE pode não existir no item quando o programa
 * é TCA (tipo_programa = 1) — nesses casos caímos no `id` geral.
 */
const CAMPOS_ID_AUTORIZACAO = [
  "id",
  "ID",
  "autorizacao_id",
  "autorizacaoId",
  "tce_id",
  "tceId",
  "id_tce",
  "id_tca",
  "id_tca_empregador",
  "tca_empregador_id",
  "tca_id",
  "estudante_id",
  "usuario_id",
];

function extrairEmpresa(item) {
  if (!item || typeof item !== "object") return null;
  for (const chave of CHAVES_EMPRESA_EM_ITEM) {
    if (item[chave] && typeof item[chave] === "object") return item[chave];
  }
  return item;
}

/**
 * Helper usado pela UI: no payload novo, cada item já vem com
 * `tipo_programa` no top-level (em vez de só no objeto embutido).
 * Lê primeiro no top-level e depois cai para o objeto empresa/estudante.
 */
export function obterTipoProgramaItem(item) {
  if (!item || typeof item !== "object") return null;

  // 1. Origem direta ou id específico de rota
  const origem = String(
    item?._origem ||
    item?.raw?._origem ||
    item?.origem ||
    item?.tipo_contrato ||
    ""
  ).toLowerCase();
  if (origem === "tce" || origem === "estagio" || item?.tce_id != null) return 0;
  if (origem === "tca_empregador" || origem === "tca-empregador" || item?.tca_empregador_id != null) return 2;
  if (origem === "tca") return 1;

  // 2. Se o próprio registro traz tipo_programa explicitamente no top-level ou raw
  const candidatosDiretos = [
    item?.tipo_programa,
    item?.tipoPrograma,
    item?.raw?.tipo_programa,
    item?.raw?.tipoPrograma,
    item?.cadastro?.tipo_programa,
  ];
  for (const c of candidatosDiretos) {
    const n = Number(c);
    if (Number.isInteger(n) && (n === 0 || n === 1 || n === 2)) return n;
  }

  // 3. Se o total_etapas é 4, é Tipo 1 (TCA Empresa padrão) apenas se NÃO houver aprendiz
  const totalEtapas = Number(
    item?.total_etapas ??
      item?.totalEtapas ??
      item?.raw?.total_etapas ??
      item?.raw?.totalEtapas,
  );
  const temAprendiz = Boolean(
    item?.nome_aprendiz ||
    item?.cpf_aprendiz ||
    item?.estudante_nome ||
    item?.estudante_cpf ||
    item?.raw?.nome_aprendiz ||
    item?.raw?.cpf_aprendiz ||
    /aprendiz|estudante/i.test(item?.status || "") ||
    /aprendiz|estudante/i.test(item?.raw?.status || "") ||
    /aprendiz|estudante/i.test(item?.status_label || "") ||
    /aprendiz|estudante/i.test(item?.raw?.status_label || "")
  );
  if (totalEtapas === 4 && !temAprendiz) return 1;
  const ehTceOrigem =
    origem === "tce" ||
    origem === "estagio" ||
    item?.tce_id != null ||
    item?.raw?.tce_id != null;
  if ((totalEtapas === 5 || temAprendiz) && !ehTceOrigem) return 2;

  const statusStr = String(
    item?.status || item?.raw?.status || "",
  ).toUpperCase();
  if (statusStr.includes("APRENDIZ") && !ehTceOrigem) {
    return 2;
  }

  // 4. Se o usuário/contexto está no módulo TCE (tipo 0)
  const tipoLogado = obterTipoProgramaUsuarioLogado();
  if (tipoLogado === 0) {
    const ehTca =
      Number(item?.tipo_programa) === 1 ||
      Number(item?.tipo_programa) === 2 ||
      Number(item?.tipoPrograma) === 1 ||
      Number(item?.tipoPrograma) === 2 ||
      origem === "tca" ||
      origem === "tca-empregador" ||
      origem === "tca_empregador";
    if (!ehTca) return 0;
  }

  // 5. Se o usuário logado está no módulo TCA Empregador (tipo 2) e o item não é explicitamente TCE (0)
  if (tipoLogado === 2) {
    const ehTce =
      Number(item?.tipo_programa) === 0 ||
      Number(item?.tipoPrograma) === 0 ||
      origem === "tce";
    if (!ehTce) return 2;
  }

  // 6. Se o usuário logado está no módulo TCA (tipo 1) e o item não é explicitamente TCE (0)
  if (tipoLogado === 1) {
    const ehTce =
      Number(item?.tipo_programa) === 0 ||
      Number(item?.tipoPrograma) === 0 ||
      origem === "tce";
    if (!ehTce) return 1;
  }

  // 6. Candidatos da empresa vinculada
  const candidatosEmpresa = [
    item?.tipo_programa_empresa,
    item?.TIPOPROGRAMA,
    item?.empresa?.tipo_programa,
    item?.empresa?.tipo_programa_empresa,
    item?.empresa?.TIPOPROGRAMA,
    item?.raw?.tipo_programa_empresa,
    item?.raw?.TIPOPROGRAMA,
  ];
  for (const c of candidatosEmpresa) {
    const n = Number(c);
    if (Number.isInteger(n) && (n === 0 || n === 1 || n === 2)) return n;
  }

  if (Number.isInteger(tipoLogado) && (tipoLogado === 0 || tipoLogado === 1 || tipoLogado === 2)) {
    return tipoLogado;
  }

  return null;
}

export function mapearOrigemParaPrograma(origem) {
  if (!origem) return null;
  const o = String(origem).toLowerCase();
  if (o === "tce") return 0;
  if (o === "tca") return 1;
  if (o === "tca_empregador" || o === "tca-empregador") return 2;
  return null;
}

/**
 * Normaliza um item de acompanhamento para o formato:
 *   {
 *     id,                    // TcaEmpregador.id (preferência) — usado em
 *                            // /tca-empregador/:id/status-completo
 *     tca_id,                // alias explícito do mesmo id
 *     empresa_id,            // Empresa.id real (JOIN) — usado para abrir
 *                            // detalhe da Empresa, NUNCA do TCA
 *     nome,
 *     documento,
 *     cnpj,                  // CNPJ/CPF preferencial (vem do top-level do item)
 *     cnpj_empresa,          // CNPJ da empresa vinculada (vem do JOIN Empresa)
 *     cnpj_ou_cpf,           // alias legado do backend
 *     tipo_programa_empresa,
 *     raw
 *   }
 * Retorna null quando o item não tem ID válido.
 */
export function normalizarEmpresaAcompanhamentoItem(item) {
  if (!item || typeof item !== "object") return null;

  const fonteEmpresa = extrairEmpresa(item);

  // id do registro (preferência do novo formato agregado).
  // No payload de `/acompanhamentos/me` (2026-09-25) cada item
  // pode trazer `id` (id da autorização) ou `autorizacao_id`. Para
  // o fluxo do TCE/TCA Empregador mantemos o id do TCA Empregador
  // quando existir, caindo para `id` top-level quando não existir.
  const idTca =
    obterIdTca(item) ??
    (() => {
      for (const campo of CAMPOS_ID_AUTORIZACAO) {
        const valor = item[campo];
        const numero = Number(valor);
        if (Number.isInteger(numero) && numero > 0) return numero;
      }
      return null;
    })();

  // DEFESA EM PROFUNDIDADE: mantém item visível mesmo sem id.
  const log = (...args) => {
    if (typeof window !== "undefined" && window.__acompDebug) {
      // eslint-disable-next-line no-console
      console.warn("[Acompanhamento/Normalizar]", ...args);
    }
  };

  if (!idTca) {
    log(
      "item sem id/autorizacao_id — incluindo com id sentinel para nao sumir da UI. Chaves disponiveis:",
      Object.keys(item),
    );
  }

  const idEmpresa = obterIdEmpresaReal(item) || idTca;

  const tipoProgramaItem = obterTipoProgramaItem(item);
  const isTce = Number(tipoProgramaItem) === 0;
  const statusStr = String(
    item?.status ||
    item?.raw?.status ||
    item?.status_label ||
    item?.raw?.status_label ||
    item?.label ||
    item?.raw?.label ||
    ""
  ).toUpperCase();

  const ehStatusAprendizOuEstudante =
    statusStr.includes("APRENDIZ") ||
    statusStr.includes("ESTUDANTE") ||
    statusStr.includes("EM_PREENCHIMENTO");

  const totalRaw = Number(
    item?.total_etapas ??
    item?.raw?.total_etapas ??
    item?.totalEtapas ??
    item?.raw?.totalEtapas
  );

  const temEtapaEstudante =
    Number(tipoProgramaItem) === 0 ||
    Number(tipoProgramaItem) === 2 ||
    totalRaw === 5 ||
    ehStatusAprendizOuEstudante;

  const isTca1 =
    !isTce &&
    !temEtapaEstudante &&
    (Number(tipoProgramaItem) === 1 ||
      Number(item?.tipo_programa) === 1 ||
      Number(item?.raw?.tipo_programa) === 1 ||
      (tipoProgramaItem == null && obterTipoProgramaUsuarioLogado() === 1));

  // Nome: para TCA=1, prioriza SEMPRE a razão social / nome da empresa,
  // omitindo completamente dados de estudante/aprendiz (já que o aprendiz não participa do fluxo).
  const nome = isTca1
    ? (
        item?.razao_social ||
        item?.razaoSocial ||
        item?.razaosocial ||
        item?.empresa_nome ||
        item?.nome_empresa ||
        item?.nome_fantasia ||
        item?.nomeFantasia ||
        item?.empresa?.razao_social ||
        item?.empresa?.razaoSocial ||
        item?.empresa?.empresa_nome ||
        item?.empresa?.nome_empresa ||
        item?.empresa?.nome ||
        item?.cadastro?.razao_social ||
        item?.cadastro?.nome ||
        fonteEmpresa?.razao_social ||
        fonteEmpresa?.razaoSocial ||
        fonteEmpresa?.empresa_nome ||
        fonteEmpresa?.nome_empresa ||
        fonteEmpresa?.nome_fantasia ||
        fonteEmpresa?.nome ||
        (item?.nome &&
        item?.nome !== item?.estudante_nome &&
        item?.nome !== item?.nome_aprendiz
          ? item.nome
          : "") ||
        ""
      )
    : (
        item?.empresa_nome ||
        item?.estudante_nome ||
        item?.nome ||
        obterNomeEmpresa(fonteEmpresa) ||
        obterNomeEmpresa(item) ||
        ""
      );

  // Documento: para TCA=1, prioriza SEMPRE o CNPJ da empresa,
  // nunca CPF de estudante/aprendiz.
  const documento = isTca1
    ? (
        item?.cnpj ||
        item?.cnpj_empresa ||
        item?.cnpj_ou_cpf ||
        item?.empresa?.cnpj ||
        item?.cadastro?.cnpj ||
        fonteEmpresa?.cnpj ||
        fonteEmpresa?.cnpj_empresa ||
        fonteEmpresa?.cnpj_ou_cpf ||
        ""
      )
    : (
        item?.estudante_cpf ||
        item?.cpf_estudante ||
        item?.cnpj ||
        obterDocumentoEmpresa(fonteEmpresa) ||
        obterDocumentoEmpresa(item) ||
        ""
      );

  // CNPJ da empresa (vem top-level no payload novo).
  const cnpjDireto =
    item?.cnpj ||
    (() => {
      for (const campo of CAMPOS_CNPJ_DIRETO) {
        const v = item[campo];
        if (typeof v === "string" && v.trim()) return v.trim();
      }
      return "";
    })();
  const cnpjDoRaw =
    item?.cnpj_empresa ||
    item?.empresa?.cnpj ||
    item?.cadastro?.cnpj ||
    item?.estudante?.cnpj ||
    "";

  return {
    id: idTca || null,
    tca_id: isTce ? null : (idTca || null),
    tce_id: item?.tce_id ?? item?.raw?.tce_id ?? (isTce ? idTca : null),
    tca_empregador_id: isTce
      ? null
      : (item?.tca_empregador_id ?? item?.raw?.tca_empregador_id ?? null),
    empresa_id: idEmpresa,
    nome,
    razao_social:
      item?.razao_social ||
      item?.razaoSocial ||
      item?.razaosocial ||
      item?.empresa_nome ||
      item?.nome_empresa ||
      item?.empresa?.razao_social ||
      item?.cadastro?.razao_social ||
      fonteEmpresa?.razao_social ||
      "",
    nome_completo:
      item?.nome_completo ||
      item?.nome ||
      item?.nome_aprendiz ||
      item?.estudante_nome ||
      "",
    documento,
    cnpj: cnpjDireto || cnpjDoRaw,
    cnpj_empresa: item?.cnpj_empresa || fonteEmpresa?.cnpj || "",
    cnpj_ou_cpf: item?.cnpj_ou_cpf || "",
    status: item?.status || item?.status_canonico || item?.raw?.status || "",
    status_label: item?.status_label || item?.label || item?.raw?.status_label || "",
    etapa_atual: item?.etapa_atual ?? item?.raw?.etapa_atual ?? null,
    total_etapas: isTca1 ? 4 : (temEtapaEstudante ? 5 : (item?.total_etapas ?? item?.raw?.total_etapas ?? (isTce ? 5 : 5))),
    tipo_programa: isTca1 ? 1 : (tipoProgramaItem ?? (isTce ? 0 : (temEtapaEstudante ? 2 : null))),
    _origem: String(
      item?._origem ||
        item?.raw?._origem ||
        (isTce
          ? "tce"
          : Number(tipoProgramaItem) === 2
            ? "tca-empregador"
            : "tca"),
    )
      .toLowerCase()
      .replace(/_/g, "-"),
    tipo_programa_empresa:
      item?.tipo_programa_empresa ||
      fonteEmpresa?.tipo_programa_empresa ||
      fonteEmpresa?.TIPOPROGRAMA ||
      "",
    nome_aprendiz:
      item?.nome_aprendiz ||
      item?.estudante_nome ||
      item?.nome_estudante ||
      item?.aprendiz_nome ||
      item?.aprendiz?.nome ||
      item?.estudante?.nome ||
      item?.cadastro?.nome_aprendiz ||
      item?.cadastro?.nome_estudante ||
      (String(item?.status || item?.raw?.status || "").toUpperCase().includes("ESTUDANTE") ? "" : (item?.nome_completo || "")) ||
      "",
    cpf_aprendiz:
      item?.cpf_aprendiz ||
      item?.estudante_cpf ||
      item?.cpf_estudante ||
      item?.aprendiz_cpf ||
      item?.cpf_aprendiz_menor_idade ||
      item?.aprendiz?.cpf ||
      item?.estudante?.cpf ||
      item?.cadastro?.cpf_aprendiz ||
      item?.cadastro?.cpf_estudante ||
      "",
    raw: item,
  };
}

export function deduplicarAcompanhamentosPorEmpresa(lista) {
  if (!Array.isArray(lista)) return [];

  const mapa = new Map();
  for (const item of lista) {
    const id =
      item?.tce_id ??
      item?.tca_empregador_id ??
      item?.tca_id ??
      item?.tcaId ??
      item?.autorizacao_id ??
      item?.id;
    if (!id) continue;
    const origRaw = String(item?._origem || item?.raw?._origem || "")
      .toLowerCase()
      .replace(/_/g, "-")
      .trim();
    const tp = obterTipoProgramaItem(item);
    const origemCanon =
      origRaw === "tce" || origRaw === "estagio" || Number(tp) === 0
        ? "tce"
        : origRaw === "tca-empregador" || Number(tp) === 2
          ? "tca-empregador"
          : origRaw === "tca" || Number(tp) === 1
            ? "tca"
            : origRaw || "doc";
    const chave = `${origemCanon}-${id}`;
    if (!mapa.has(chave)) {
      mapa.set(chave, item);
    } else {
      const existente = mapa.get(chave);
      mapa.set(chave, {
        ...item,
        ...existente,
        razao_social: existente.razao_social || item.razao_social || "",
        cnpj: existente.cnpj || item.cnpj || "",
        nome_aprendiz: existente.nome_aprendiz || item.nome_aprendiz || "",
        cpf_aprendiz: existente.cpf_aprendiz || item.cpf_aprendiz || "",
        etapa_atual: existente.etapa_atual ?? item.etapa_atual ?? null,
      });
    }
  }
  return Array.from(mapa.values());
}
